<template>
    <page-loading v-if="loading"></page-loading>

    <vx-alert v-else-if="loadError" color="danger" :closeable="false" data-test="load-error">{{ loadError }}</vx-alert>

    <vx-expandable-card v-else>
        <template v-slot:header>
            <div class="flex items-center gap-2">
                Configuration
                <vx-badge v-if="!saved?.company_id" size="xs" color="amber" data-test="not-configured">
                    Not configured
                </vx-badge>
            </div>
        </template>
        <template v-slot:content>
            <div class="flex flex-col gap-6 mt-3">
                <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <dt class="text-sm text-slate-500">Marketing subscribers</dt>
                        <dd class="text-lg font-bold" data-test="subscriber-count">
                            {{ formatNumber(subscriberCount) }}
                        </dd>
                    </div>
                </dl>

                <kiosk-rnd-run-controls
                    :teamId="props.teamId"
                    :teamName="teamName"
                    :companyId="saved?.company_id ?? null"
                    :subscriberCount="subscriberCount"
                    :latestRun="latestRun"
                    :activeRun="activeRun"
                    :followableRun="followableRun"
                    @changed="emit('changed')"
                ></kiosk-rnd-run-controls>

                <div>
                    <vx-label size="md">RND Company ID</vx-label>
                    <vx-input
                        v-model="data.company_id"
                        size="lg"
                        maxlength="10"
                        placeholder="C123456789"
                        hint="Created in the RND portal once the client's Letter of Authorization is approved."
                        data-test="company-id-input"
                        :error="displayFirstError(validation.company_id)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Opt-in date attribute</vx-label>
                    <kiosk-rnd-attribute-key-select
                        v-model="data.consent_attribute_key"
                        :teamId="props.teamId"
                        :error="displayFirstError($externalResults.consent_attribute_key)"
                    ></kiosk-rnd-attribute-key-select>
                    <div class="mt-1 text-sm text-slate-500">
                        Read from the contact's corporate record as YYYY-MM-DD. Without a valid value, the consent date
                        is the Voxie opt-in timestamp.
                    </div>
                </div>

                <div>
                    <div class="flex items-center justify-between">
                        <div>
                            <vx-label :id="autoRunLabelId" size="md">Monthly auto-run</vx-label>
                            <div :id="autoRunDescriptionId" class="text-sm text-slate-500">
                                Checks every marketing subscriber at 08:00 US Eastern on the 17th of each month. If it
                                is switched on after this month's check, the first run is next month.
                            </div>
                        </div>
                        <vx-toggle
                            v-model="data.auto_run"
                            size="lg"
                            :disabled="!data.company_id && !data.auto_run"
                            :aria-labelledby="autoRunLabelId"
                            :aria-describedby="autoRunDescriptionId"
                            data-test="auto-run-toggle"
                        ></vx-toggle>
                    </div>
                    <div
                        v-if="displayFirstError($externalResults.auto_run)"
                        class="mt-2 px-0.5 text-xs text-rose-800"
                        data-test="auto-run-error"
                    >
                        {{ displayFirstError($externalResults.auto_run) }}
                    </div>
                </div>

                <vx-alert
                    v-if="saved?.auto_run && !data.company_id"
                    color="info"
                    :closeable="false"
                    data-test="auto-run-warning"
                >
                    Saving without a Company ID switches monthly auto-run off.
                </vx-alert>

                <vx-button size="lg" color="primary" :loading="processing" data-test="save-button" @click="submit()">
                    Save
                </vx-button>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxAlert, VxBadge, VxButton, VxExpandableCard, VxInput, VxLabel, VxToggle } from '@voxie/frontend-components';
import { helpers } from '@vuelidate/validators';
import { isCancel } from 'axios';
import { ref, useId, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskRndAttributeKeySelect from '~/components/kiosk/kiosk-reassigned-numbers/KioskRndAttributeKeySelect.vue';
import KioskRndRunControls from '~/components/kiosk/kiosk-reassigned-numbers/runs/KioskRndRunControls.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun, RndTeamConfig, RndTeamConfigPayload, RndTeamDetails } from '~/services/adminRndClient.types';
import { formatNumber } from '~/utils/helpers';
import { catchRequestError } from '~/utils/request';
import { displayFirstError } from '~/utils/validation';

const props = defineProps<{ teamId: number; refreshKey: number }>();
const emit = defineEmits<{ changed: [] }>();

const toasted = useToasted();

const teamCancelKey = Symbol('kiosk-rnd-team');

const autoRunLabelId = useId();
const autoRunDescriptionId = useId();

const loading = ref(true);
const loadError = ref('');
const saved = ref<RndTeamConfig>();
const subscriberCount = ref(0);
const teamName = ref('');
const latestRun = ref<RndRun | null>(null);
const activeRun = ref<RndRun | null>(null);
const followableRun = ref<RndRun | null>(null);
const autoRunSwitchedOff = ref(false);
const data = ref<RndTeamConfigPayload>({ company_id: null, consent_attribute_key: null, auto_run: false });

const applyConfig = (config: RndTeamConfig) => {
    saved.value = config;
    data.value = {
        company_id: config.company_id,
        consent_attribute_key: config.consent_attribute_key,
        auto_run: config.auto_run,
    };
};

const showTeamFacts = (details: RndTeamDetails) => {
    teamName.value = details.team_name;
    subscriberCount.value = details.marketing_subscriber_count;
    latestRun.value = details.latest_run;
    activeRun.value = details.active_run;
    followableRun.value = details.followable_run;
};

const { submit, processing, validation, $externalResults } = useFormSubmission({
    data,
    validation: {
        company_id: {
            companyId: helpers.withMessage(
                'The RND Company ID must be 3 to 10 letters and digits.',
                helpers.regex(/^[A-Za-z0-9]{3,10}$/)
            ),
        },
    },
    submit: async () => {
        const response = await adminRndClient.updateTeam(props.teamId, {
            ...data.value,
            company_id: data.value.company_id || null,
        });

        applyConfig(response.data.config);
        autoRunSwitchedOff.value = response.data.auto_run_switched_off;
        emit('changed');

        return response;
    },
    notification: () => ({
        message: autoRunSwitchedOff.value
            ? 'Settings saved. Monthly auto-run was switched off because the team has no RND Company ID.'
            : 'Settings saved',
    }),
    preserveScroll: true,
});

adminRndClient
    .getTeam(props.teamId)
    .then((response) => {
        applyConfig(response.data.config);
        showTeamFacts(response.data);
    })
    .catch(() => {
        loadError.value = 'Failed to load the team. Please try again.';
    })
    .finally(() => {
        loading.value = false;
    });

const refreshTeam = async () => {
    try {
        const { data: details } = await adminRndClient.getTeam(props.teamId, { cancelKey: teamCancelKey });

        saved.value = details.config;
        showTeamFacts(details);
    } catch (error) {
        if (!isCancel(error)) {
            toasted.error(catchRequestError(error));
        }
    }
};

watch(
    () => props.refreshKey,
    () => {
        refreshTeam();
    }
);
</script>
